/**
 * InvoiceTimeline.tsx
 *
 * Right-rail "Activity" card. Calls GET /api/v1/invoice/transactionLines/:id/
 * timeline and renders the unified event stream as a vertical timeline.
 *
 * Backed by data we already had (Jobs, Invoices, approvals, JobExports). The
 * backend handler aggregates and sorts; this component is presentation-only.
 *
 * Color mapping:
 *   extracted, system events       -> grey
 *   approval_required              -> blue
 *   approval_voted (approved)      -> green
 *   approval_voted (rejected)      -> red
 *   duplicate_detected             -> red
 *   exported                       -> green (red on failure)
 *   po_matched / bank_matched      -> green
 */
import React, { useEffect, useState } from "react";
import { Spinner, Alert } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  getInvoiceTimeline,
  InvoiceTimelineEvent,
} from "src/docuclipper/api";

interface Props {
  transactionLineId?: number;
}

function relativeTime(iso: string): string {
  const t = new Date(iso).getTime();
  if (!isFinite(t)) return iso;
  const diffMs = Date.now() - t;
  const sec = Math.round(diffMs / 1000);
  if (sec < 60) return `${Math.max(sec, 0)}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.round(hr / 24);
  if (d < 30) return `${d}d ago`;
  const mo = Math.round(d / 30);
  if (mo < 12) return `${mo}mo ago`;
  return `${Math.round(mo / 12)}y ago`;
}

function colorForEvent(ev: InvoiceTimelineEvent): string {
  switch (ev.type) {
    case "approval_required":
    case "approval_submitted":
      return "#0d6efd"; // blue
    case "approval_voted":
      return ev.detail?.vote === "rejected" ? "#dc3545" : "#198754";
    case "duplicate_detected":
      return "#dc3545";
    case "exported":
      return ev.detail?.error ? "#dc3545" : "#198754";
    case "po_matched":
    case "bank_matched":
    case "vendor_matched":
      return "#198754";
    default:
      return "#6c757d";
  }
}

function iconForEvent(ev: InvoiceTimelineEvent): any {
  switch (ev.type) {
    case "extracted":
      return "file-alt";
    case "vendor_matched":
      return "link";
    case "duplicate_detected":
      return "copy";
    case "approval_required":
      return "user-clock";
    case "approval_submitted":
      return "paper-plane";
    case "approval_voted":
      return ev.detail?.vote === "rejected" ? "times-circle" : "check-circle";
    case "exported":
      return "file-export";
    case "po_matched":
      return "file-invoice";
    case "bank_matched":
      return "university";
    default:
      return "circle";
  }
}

function actorInitials(name?: string): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export const InvoiceTimeline: React.FC<Props> = ({ transactionLineId }) => {
  const [events, setEvents] = useState<InvoiceTimelineEvent[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!transactionLineId) {
      setEvents(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    getInvoiceTimeline(transactionLineId)
      .then((res) => {
        if (cancelled) return;
        // Render newest first in the UI per user expectation for activity
        // feeds; backend returns ascending so we reverse here.
        setEvents([...(res.events || [])].reverse());
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.message || "Failed to load activity");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [transactionLineId]);

  return (
    <div
      className="mb-4 p-3"
      style={{
        backgroundColor: "#ffffff",
        borderRadius: "8px",
        border: "1px solid #e9ecef",
        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
      }}
      data-cy="invoice-section-activity"
    >
      <h6 className="mb-3 text-primary">
        <FontAwesomeIcon icon="stream" className="mr-2" />
        Activity
      </h6>

      {loading && (
        <div className="text-center text-muted py-2">
          <Spinner animation="border" size="sm" /> Loading activity
        </div>
      )}

      {error && (
        <Alert variant="warning" className="mb-0">
          {error}
        </Alert>
      )}

      {!loading && !error && events && events.length === 0 && (
        <div className="text-center text-muted small">
          No activity recorded yet
        </div>
      )}

      {!loading && !error && events && events.length > 0 && (
        <ul
          className="list-unstyled mb-0"
          style={{ position: "relative", paddingLeft: "1.5rem" }}
        >
          {/* Vertical rail */}
          <span
            style={{
              position: "absolute",
              left: "0.55rem",
              top: 0,
              bottom: 0,
              width: "2px",
              backgroundColor: "#e9ecef",
            }}
          />
          {events.map((ev, i) => {
            const color = colorForEvent(ev);
            const initials = actorInitials(ev.actor?.name);
            return (
              <li
                key={`${ev.type}-${ev.timestamp}-${i}`}
                style={{ position: "relative", paddingBottom: "0.85rem" }}
              >
                <span
                  style={{
                    position: "absolute",
                    left: "-1.3rem",
                    top: "0.15rem",
                    width: "1.1rem",
                    height: "1.1rem",
                    borderRadius: "50%",
                    backgroundColor: color,
                    color: "#fff",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "0.6rem",
                  }}
                  title={ev.type}
                >
                  <FontAwesomeIcon icon={iconForEvent(ev)} />
                </span>
                <div style={{ fontSize: "0.85rem", lineHeight: 1.3 }}>
                  <div>
                    <span
                      title={new Date(ev.timestamp).toLocaleString()}
                      className="text-muted mr-2"
                      style={{ fontSize: "0.75rem" }}
                    >
                      {relativeTime(ev.timestamp)}
                    </span>
                    {ev.actor?.type === "user" && (
                      <span
                        className="mr-1"
                        style={{
                          display: "inline-block",
                          width: "1rem",
                          height: "1rem",
                          borderRadius: "50%",
                          backgroundColor: "#dee2e6",
                          color: "#495057",
                          fontSize: "0.55rem",
                          textAlign: "center",
                          lineHeight: "1rem",
                          verticalAlign: "middle",
                        }}
                        title={ev.actor.email || ev.actor.name}
                      >
                        {initials}
                      </span>
                    )}
                  </div>
                  <div>{ev.summary}</div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default InvoiceTimeline;
